import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import {
  RegistrationFormRule,
  RegistrationFormSchema,
} from "@factory/form/form.rule";
import { useFormDefaultRegistration } from "@factory/form/form-default-registration.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  useQyUpdateRegistration,
  useQyGetRegistrationById,
  useQyDeleteRegistration,
} from "@factory/query/registration.query";
import { GetResponseRegistrationModel } from "@factory/model/query-model-registration";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditRegistration() {
  // LOCAL DECLARATION
  const { formatDateForInput } = useDateTimeContext();
  const { getDefault } = useFormDefaultRegistration();
  const navigate = useNavigate();
  const { mapUpdateRegistration, mapDeleteRegistration } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { registrationId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Registration
  const handleGetApiSuccess = (data: GetResponseRegistrationModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("tenant", responseData?.tenant || "");
    setValue("firstName", responseData?.first_name || "");
    setValue("middleName", responseData?.middle_name || "");
    setValue("lastName", responseData?.last_name || "");
    if (responseData?.birthday) {
      setValue("birthday", formatDateForInput(responseData?.birthday));
    }
    setValue("gender", responseData?.gender || "");
    setValue("address", responseData?.address || "");
    setValue("mobileNumber", responseData?.mobile_number || "");
    setValue("avatar", responseData?.avatar || "");
    setValue("email", responseData?.email || "");
    setValue("isTakingMedication", Boolean(responseData?.is_taking_medication));
    setValue("medicationDetails", responseData?.medication_details || "");
    setValue("hasAllergies", Boolean(responseData?.has_allergies));
    setValue("allergyDetails", responseData?.allergy_details || "");
    setValue(
      "hasMedicalConditions",
      Boolean(responseData?.has_medical_conditions)
    );
    setValue(
      "medicalConditonDetails",
      responseData?.medical_conditon_details || ""
    );
    setValue("wasRecentlyUnwell", Boolean(responseData?.was_recently_unwell));
    setValue("unwellDetails", responseData?.unwell_details || "");
    setValue(
      "otherMedicalConditions",
      responseData?.other_medical_conditions || ""
    );
    setValue("service", responseData?.service || "");
    if (responseData?.date) {
      setValue("date", formatDateForInput(responseData?.date));
    }
    setValue("time", responseData?.time || "");
    setValue("otherRemarks", responseData?.other_remarks || "");
    setValue("status", responseData?.status || "");
  };
  const {
    data: registrationResponse,
    isLoading,
    isError: registrationError,
  } = useQyGetRegistrationById(registrationId || "", handleGetApiSuccess);
  const registrationData = registrationResponse?.data?.[0];

  // API UPDATE Registration
  const handleUpdateApiSuccess = () => {
    showSuccess("Registration updated successfully");
  };
  const { mutate: updateRegistration, isLoading: isEditLoading } =
    useQyUpdateRegistration(handleUpdateApiSuccess);

  // API DELETE Registration
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Registration deleted successfully");
  };
  const { mutate: deleteRegistration } = useQyDeleteRegistration(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<RegistrationFormSchema>({
    defaultValues: getDefault(registrationData),
    resolver: zodResolver(RegistrationFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: RegistrationFormSchema) => {
    if (!registrationData || !registrationData.code) {
      throw new Error("No registration data");
    }

    const formData = mapUpdateRegistration(form, registrationData.code);
    updateRegistration(formData);
  };
  const handleValidateError = (err: FieldErrors<RegistrationFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!registrationData || !registrationData.code) {
      throw new Error("No registration data found");
    }

    const form = mapDeleteRegistration(registrationData.code);
    deleteRegistration(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    registrationId,
    registrationData,
    isLoading,
    registrationError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
